// Service d'authentification pour gérer les connexions Google et invité

import { retryDelayMs } from '../utils/fetchRetry';

// Configuration de l'API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

/**
 * Vérifie si l'utilisateur est authentifié
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const authMode = localStorage.getItem('auth_mode');
  const user = localStorage.getItem('user');
  return !!(authMode && user);
};

/**
 * Récupère l'utilisateur courant
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Erreur parsing user:', error);
    return null;
  }
};

/**
 * Récupère le mode d'authentification (google ou guest)
 * @returns {string|null}
 */
export const getAuthMode = () => {
  return localStorage.getItem('auth_mode');
};

/**
 * Récupère le token d'accès
 * @returns {string|null}
 */
export const getAccessToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * Déconnexion de l'utilisateur
 */
export const logout = () => {
  localStorage.removeItem('auth_mode');
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  
  // Redirection vers la page de connexion
  window.location.href = '/login';
};

/**
 * Rafraîchit le token d'accès (pour Google OAuth)
 * @returns {Promise<string|null>}
 */
// Un seul renouvellement à la fois : une page qui charge dix segments déclenchait dix
// appels à /api/auth/refresh en parallèle. Les échecs sont comptés par authLimiter
// (5 par 15 min) : la rafale suffisait à bloquer /api/auth, connexion comprise.
let refreshInFlight = null;

const doRefresh = async (refreshToken) => {
  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    localStorage.setItem('access_token', data.access_token);

    return data.access_token;
  } catch (error) {
    console.error('Erreur refresh token:', error);
    logout(); // Déconnexion si le refresh échoue
    return null;
  }
};

export const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    // Session ouverte avant l'émission des jetons de renouvellement, ou jeton effacé :
    // il n'y a rien à renouveler. On déconnecte franchement au lieu de laisser
    // l'utilisateur sur une page dont chaque requête échoue en silence.
    console.warn('⚠️ Aucun jeton de renouvellement : reconnexion nécessaire');
    logout();
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = doRefresh(refreshToken).finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

/**
 * Valide que le token correspond à l'utilisateur en session
 * @returns {boolean}
 */
export const validateSession = () => {
  const token = getAccessToken();
  const user = getCurrentUser();
  const authMode = getAuthMode();
  
  // Pas d'authentification requise si pas de mode auth
  if (!authMode) {
    return false;
  }

  // Mode invité n'est plus supporté
  if (authMode === 'guest') {
    logout();
    return false;
  }

  // Vérifier que token et user existent
  if (!token || !user) {
    console.warn('⚠️ Session invalide: token ou user manquant');
    logout();
    return false;
  }
  
  // Décoder le token pour vérifier qu'il correspond à l'utilisateur
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.userId !== user.id) {
      console.error('🚨 ALERTE SÉCURITÉ: Token/User mismatch! Token userId:', payload.userId, 'User id:', user.id);
      logout();
      return false;
    }
    
    // Vérifier l'expiration du token
    if (payload.exp && payload.exp < Date.now() / 1000) {
      console.warn('⏰ Token expiré, tentative de rafraîchissement...');
      return false; // Le refreshAccessToken sera appelé automatiquement
    }
  } catch (e) {
    console.error('❌ Erreur décodage token:', e);
    logout();
    return false;
  }
  
  return true;
};

/**
 * Effectue une requête API avec authentification
 * @param {string} url 
 * @param {Object} options 
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (url, options = {}) => {
  // Valider la session avant chaque requête
  let sessionValid = validateSession();
  
  // Si session invalide à cause du token expiré, tenter le refresh
  if (!sessionValid) {
    const token = getAccessToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Si le token existe mais est expiré, tenter un refresh
        if (payload.exp && payload.exp < Date.now() / 1000) {
          console.log('⏰ Token expiré, tentative de rafraîchissement automatique...');
          const newToken = await refreshAccessToken();
          if (newToken) {
            sessionValid = true;
          }
        }
      } catch (e) {
        // Token malformé, on continue avec l'erreur
      }
    }
  }
  
  if (!sessionValid) {
    // Le renouvellement a échoué ou n'était pas possible : la session est morte. On
    // renvoie vers la page de connexion au lieu de laisser la page en place à répéter
    // la même erreur sur chaque requête.
    logout();
    throw new Error('Session expirée - veuillez vous reconnecter');
  }

  const token = getAccessToken();

  // Configuration des headers
  const headers = {
    ...options.headers,
  };

  // Toujours ajouter le token s'il est présent
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('⚠️ [FRONT] authenticatedFetch: Pas de token disponible !');
  }

  console.log(`🚀 [FRONT] Fetching: ${url}`, { headers: Object.keys(headers) });

  try {
    let response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`📥 [FRONT] Response: ${url} - Status: ${response.status}`);

    // Si 401, tenter de rafraîchir le token
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          ...options,
          headers,
        });
      } else {
        // Si le refresh échoue ou n'est pas possible, déconnexion forcée
        logout();
        throw new Error('Session expirée');
      }
    }

    // Si 429 (Too Many Requests / rate limit), attendre le délai demandé par le serveur
    // (Retry-After, plafonné à 10 s) et réessayer jusqu'à deux fois : une requête refusée
    // en 429 n'a pas été traitée, la renvoyer est sans risque.
    for (let attempt = 0; response.status === 429 && attempt < 2; attempt++) {
      console.warn('⚠️ [FRONT] Rate limit atteint (429), nouvelle tentative...');
      const delayMs = process.env.NODE_ENV === 'test' ? 0 : retryDelayMs(response, attempt, { maxMs: 10000 });
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      response = await fetch(url, {
        ...options,
        headers,
      });
    }

    // Si toujours 401 après tentative de refresh
    if (response.status === 401) {
      logout();
      throw new Error('Session expirée');
    }

    return response;
  } catch (error) {
    console.error('Erreur authenticated fetch:', error);
    throw error;
  }
};

/**
 * Vérifie si l'utilisateur est en mode invité
 * @returns {boolean}
 */
export const isGuestMode = () => {
  return false; // Mode invité désactivé définitivement
};

/**
 * Vérifie si l'utilisateur est connecté avec Google
 * @returns {boolean}
 */
export const isGoogleAuth = () => {
  return getAuthMode() === 'google';
};

/**
 * Récupère les informations de profil utilisateur
 * @returns {Object}
 */
export const getUserProfile = () => {
  const user = getCurrentUser();
  const authMode = getAuthMode();

  return {
    ...user,
    isGuest: authMode === 'guest',
    isGoogle: authMode === 'google',
    canSync: authMode === 'google', // Synchronisation cloud uniquement pour Google
  };
};

const authService = {
  isAuthenticated,
  getCurrentUser,
  getAuthMode,
  getAccessToken,
  logout,
  refreshAccessToken,
  authenticatedFetch,
  isGuestMode,
  isGoogleAuth,
  getUserProfile,
  validateSession,
};

export default authService;
